import { Component, ElementRef, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ScreenType, NadajPrzesylkeType } from 'src/app/enums/enums';
import { FormService } from 'src/app/services/form.service';
import { firstValueFrom } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { FormFieldErrorMessagesComponent } from '../form-field-error-messages/form-field-error-messages.component';
import { SubmitButtonComponent } from '../submit-button/submit-button.component';
import { HttpErrorResponse } from '@angular/common/http';
import { PreAuthRequest } from 'src/app/models/pre-auth/pre-auth-request.model';
import { PreAuthResponse } from 'src/app/models/pre-auth/pre-auth-response.model';
import { FormErrorMessageComponent } from '../form-error-message/form-error-message.component';
import { StoreService } from 'src/app/services/store.service';

@Component({
  selector: 'app-captcha-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SubmitButtonComponent,
    FormFieldErrorMessagesComponent,
    FormErrorMessageComponent,
  ],
  templateUrl: './captcha-dialog.component.html',
  styleUrl: './captcha-dialog.component.scss',
})
export class CaptchaDialogComponent {
  @ViewChild('captchaDialog') captchaDialog!: ElementRef<HTMLDialogElement>;

  ScreenType = ScreenType;
  NadajPrzesylkeType = NadajPrzesylkeType;

  captchaForm!: FormGroup;
  captchaImage!: string;
  captchaLoadingError = signal(false);
  isSubmitLoading = signal(false);
  submitException = signal(false);

  openDialog(): void {
    if (this.captchaDialog.nativeElement) {
      this.captchaDialog.nativeElement.showModal();
    }
  }

  closeDialog(): void {
    if (this.captchaDialog.nativeElement) {
      this.captchaDialog.nativeElement.close();
    }
  }

  constructor(
    private apiService: ApiService,
    private formBuilder: FormBuilder,
    protected storeService: StoreService,
    protected formService: FormService
  ) {
    this.initializeForm();
  }

  private initializeForm() {
    this.captchaForm = this.formBuilder.group({
      captcha_input: [null, [Validators.required]],
    });
  }

  get captchaInput() {
    return this.captchaForm.get('captcha_input');
  }

  async reloadCaptcha() {
    this.captchaLoadingError.set(false);
    this.captchaInput?.setValue(undefined);
    this.captchaInput?.markAsUntouched();
    this.captchaInput?.setErrors(null);

    this.storeService.setCaptchaResponse(undefined);
    try {
      const captchaResponse = await firstValueFrom(
        this.apiService.submitCaptchaRequest()
      );
      this.storeService.setCaptchaResponse(captchaResponse);
    } catch (error) {
      this.captchaLoadingError.set(true);
    }
  }

  async onSubmit() {
    this.isSubmitLoading.set(true);

    if (this.captchaForm.invalid) {
      this.formService.markAllAsTouched(this.captchaForm);
      this.isSubmitLoading.set(false);
      return;
    }
    await this.submitPreAuthRequest();
  }

  private async submitPreAuthRequest() {
    const preAuthRequest: PreAuthRequest = {
      captchaResponse: {
        cptId: this.storeService.getCptId(),
        captchaAnswer: this.captchaForm.get('captcha_input')?.value,
      },
      preAuthData: {
        shipNo: this.storeService.getShipNo(),
        zipCode: this.storeService.getZipCode(),
        payerNo: this.storeService.getPayerNo(),
      },
    };

    this.storeService.setPreAuthRequest(preAuthRequest);

    try {
      const preAuthResponse = await firstValueFrom(
        this.apiService.submitPreAuthRequest(preAuthRequest)
      );

      if (preAuthResponse) {
        this.handlePreAuthResponse(preAuthResponse);
      } else {
        throw new Error('PreAuthResponse is undefined');
      }
    } catch (error) {
      this.handlePreAuthError(error as HttpErrorResponse);
    }
  }

  private handlePreAuthResponse(preAuthResponse: PreAuthResponse) {
    this.submitException.set(false);
    this.storeService.setPreAuthResponse(preAuthResponse);
    this.isSubmitLoading.set(false);
    this.captchaForm.patchValue({
      captcha_input: undefined,
    });
    this.formService.markAllAsUntouched(this.captchaForm);
    this.closeDialog();
  }

  private handlePreAuthError(error: HttpErrorResponse) {
    if (error.error.httpStatus !== 500) {
      if (error.error && error.error.errorMsg) {
        error.error.errorMsg.forEach((err: any) => {
          this.handleFieldErrors(err);
        });
      } else {
        this.reloadCaptcha();
        this.submitException.set(true);
      }
    } else {
      this.reloadCaptcha();
      this.submitException.set(true);
    }

    this.isSubmitLoading.set(false);
  }

  private handleFieldErrors(err: any) {
    this.reloadCaptcha();
    if (err.field === 'captchaAnswer') {
      const control = this.captchaForm.get('captcha_input');
      control?.setErrors({ invalid: true });
      control?.markAsTouched();
    }
  }
}
